// Leave Overlay Badge Component
// Part of LEAVE_OVERLAY_IMPLEMENTATION.md - Phase 3

import { useNavigate } from 'react-router-dom';
import { Palmtree } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { LeaveOverlay } from '@/services/leave/leaveOverlayTypes';

interface LeaveOverlayBadgeProps {
  leave: LeaveOverlay;
  /** Show employee name (true for manager view, false for employee view) */
  showName?: boolean;
  /** Compact mode for smaller displays */
  compact?: boolean;
  className?: string;
  /** When true, navigates to /leave (employee view) instead of /manager/leave */
  isEmployeeView?: boolean;
}

/**
 * Badge displaying leave information in schedule views
 * Clicking navigates to leave request details
 */
export function LeaveOverlayBadge({
  leave,
  showName = true,
  compact = false,
  className,
  isEmployeeView = false,
}: LeaveOverlayBadgeProps) {
  const navigate = useNavigate();

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Navigate to appropriate leave page based on context
    const basePath = isEmployeeView ? '/leave' : '/manager/leave';
    navigate(`${basePath}?request=${leave.leave_request_id}`);
  };

  // Format leave type for display
  const formatLeaveType = (type: string) => {
    return type
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, c => c.toUpperCase());
  };

  if (compact) {
    return (
      <button
        onClick={handleClick}
        className={cn(
          "inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium",
          "bg-amber-100 text-amber-800 border border-amber-200",
          "dark:bg-amber-900/30 dark:text-amber-200 dark:border-amber-700",
          "hover:bg-amber-200 dark:hover:bg-amber-900/50",
          "cursor-pointer transition-colors",
          className
        )}
        title={`${leave.profile_name} - ${formatLeaveType(leave.leave_type)}`}
      >
        <Palmtree className="h-3 w-3 flex-shrink-0" />
        <span className="truncate max-w-[80px]">
          {showName ? leave.profile_name : formatLeaveType(leave.leave_type)}
        </span>
      </button>
    );
  }

  return (
    <button
      onClick={handleClick}
      className={cn(
        "flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium",
        "bg-amber-100 text-amber-800 border border-amber-200",
        "dark:bg-amber-900/30 dark:text-amber-200 dark:border-amber-700",
        "hover:bg-amber-200 dark:hover:bg-amber-900/50",
        "cursor-pointer transition-colors w-full",
        className
      )}
    >
      <Palmtree className="h-4 w-4 flex-shrink-0" />
      <div className="flex flex-col items-start min-w-0">
        {showName && (
          <span className="font-medium truncate w-full">{leave.profile_name}</span>
        )}
        <span className="text-xs opacity-80 truncate w-full">
          {formatLeaveType(leave.leave_type)}
        </span>
      </div>
    </button>
  );
}
